import React, { useState, useEffect, useMemo } from 'react';
import { getAllExercises } from '../services/api';
import { useLocalizedExerciseOptions } from '../i18n/useLocalizedExerciseOptions';
import {
  FullScreenModal,
  FullScreenModalContent,
  FullScreenModalHeader,
  FullScreenModalTitle,
  FullScreenModalBackButton,
  FullScreenModalBody,
} from './ui/fullscreen-modal';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Dumbbell,
  Plus,
  X,
  GripVertical,
  Edit2,
  Trash2,
  Check,
  FileText,
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import PlanAssignmentModal from './PlanAssignmentModal';

// Sortable Exercise Item
const SortableExerciseItem = ({ exercise, index, onEdit, onDelete, onUpdate }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: exercise.id || `exercise-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [isEditing, setIsEditing] = useState(false);
  const [editSets, setEditSets] = useState(exercise.sets || [{ reps: '', weight: '' }]);

  const handleSaveEdit = () => {
    onUpdate({ ...exercise, sets: editSets });
    setIsEditing(false);
  };

  const handleAddSet = () => {
    setEditSets([...editSets, { reps: '', weight: '' }]);
  };

  const handleRemoveSet = (setIndex) => {
    setEditSets(editSets.filter((_, idx) => idx !== setIndex));
  };

  const handleSetChange = (setIndex, field, value) => {
    const newSets = [...editSets];
    newSets[setIndex] = { ...newSets[setIndex], [field]: value };
    setEditSets(newSets);
  };

  if (isEditing) {
    return (
      <div className="p-4 border rounded-lg bg-background space-y-3">
        <div className="flex items-center justify-between">
          <span className="font-medium">{exercise.name}</span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={handleSaveEdit}
            >
              <Check className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setIsEditing(false);
                setEditSets(exercise.sets || [{ reps: '', weight: '' }]);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="space-y-2">
          {editSets.map((set, setIndex) => (
            <div key={setIndex} className="flex gap-2 items-center">
              <span className="text-sm text-muted-foreground w-8">Подход {setIndex + 1}</span>
              <Input
                type="number"
                placeholder="Повторения"
                value={set.reps}
                onChange={(e) => handleSetChange(setIndex, 'reps', e.target.value)}
                className="flex-1"
              />
              <Input
                type="number"
                placeholder="Вес (кг)"
                value={set.weight}
                onChange={(e) => handleSetChange(setIndex, 'weight', e.target.value)}
                className="flex-1"
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleRemoveSet(setIndex)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button
            size="sm"
            variant="outline"
            onClick={handleAddSet}
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            Добавить подход
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-4 border rounded-lg bg-background hover:bg-accent/50 transition-colors"
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
      >
        <GripVertical className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{exercise.name}</div>
        <div className="text-sm text-muted-foreground">
          {exercise.sets && exercise.sets.length > 0 ? (
            exercise.sets.map((set, idx) => (
              <span key={idx}>
                {idx > 0 && ', '}
                {set.reps || '?'} × {set.weight ? `${set.weight} кг` : '?'}
              </span>
            ))
          ) : (
            'Нет подходов'
          )}
        </div>
      </div>
      <div className="flex gap-1">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setIsEditing(true)}
        >
          <Edit2 className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onDelete(exercise.id || `exercise-${index}`)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

const ExercisePlanModal = ({ show, onHide, exercises = [], onSave, trainerId = null, onPlanSelect = null }) => {
  const [exerciseList, setExerciseList] = useState(exercises.map((ex, idx) => ({
    ...ex,
    id: ex.id || `exercise-${idx}`
  })));
  const [allExercises, setAllExercises] = useState([]);
  const localizedAllExercises = useLocalizedExerciseOptions(allExercises);
  const [showAddExercise, setShowAddExercise] = useState(false);
  const [showPlanSelector, setShowPlanSelector] = useState(false);
  const [exerciseSearch, setExerciseSearch] = useState('');
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [newExerciseSets, setNewExerciseSets] = useState([{ reps: '', weight: '' }]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (show) {
      setExerciseList(exercises.map((ex, idx) => ({
        ...ex,
        id: ex.id || `exercise-${idx}`
      })));
      if (trainerId) {
        loadExercises();
      }
    }
  }, [show, exercises, trainerId]);

  const loadExercises = async () => {
    try {
      const response = await getAllExercises(trainerId);
      setAllExercises(response.data || []);
    } catch (err) {
      console.error('Ошибка загрузки упражнений:', err);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setExerciseList((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleAddExercise = () => {
    if (!selectedExercise) return;

    const canonicalName =
      typeof selectedExercise === 'string'
        ? selectedExercise
        : (selectedExercise.value || selectedExercise.name || '').trim() ||
          selectedExercise.label ||
          '';
    const newExercise = {
      id: `exercise-${Date.now()}`,
      name: canonicalName,
      sets: newExerciseSets.filter(s => s.reps || s.weight)
    };

    setExerciseList([...exerciseList, newExercise]);
    setShowAddExercise(false);
    setSelectedExercise(null);
    setNewExerciseSets([{ reps: '', weight: '' }]);
    setExerciseSearch('');
  };

  const handleDeleteExercise = (id) => {
    setExerciseList(exerciseList.filter(ex => ex.id !== id));
  };

  const handleUpdateExercise = (updatedExercise) => {
    setExerciseList(exerciseList.map(ex => 
      ex.id === updatedExercise.id ? updatedExercise : ex
    ));
  };

  const handleSave = () => {
    onSave(exerciseList);
    onHide();
  };

  const handlePlanSelectFromModal = (plan) => {
    if (!plan || !plan.exercises || plan.exercises.length === 0) {
      return;
    }

    // Копируем упражнения из плана
    const exercises = plan.exercises.map(ex => {
      let sets = ex.default_sets || ex.sets || [];
      
      if (typeof sets === 'string') {
        try {
          sets = JSON.parse(sets);
        } catch (e) {
          console.error('Ошибка парсинга sets:', e);
          sets = [];
        }
      }
      
      if (!Array.isArray(sets) || sets.length === 0) {
        sets = [{ reps: '', weight: '' }];
      }
      
      sets = sets.map(set => ({
        reps: set.reps !== undefined ? set.reps : '',
        weight: set.weight !== undefined ? set.weight : ''
      }));

      return {
        id: `exercise-${Date.now()}-${Math.random()}`,
        name: ex.exercise_name || ex.name,
        sets: sets
      };
    });

    // Добавляем упражнения к существующим
    setExerciseList([...exerciseList, ...exercises]);
    setShowPlanSelector(false);
    
    if (onPlanSelect) {
      onPlanSelect(plan);
    }
  };

  const filteredExercises = useMemo(() => {
    const list = localizedAllExercises;
    if (!exerciseSearch) return list.slice(0, 10);
    const q = exerciseSearch.toLowerCase();
    return list.filter(
      (ex) =>
        (ex.label || '').toLowerCase().includes(q) ||
        (ex.value || '').toLowerCase().includes(q) ||
        (ex.name || '').toLowerCase().includes(q)
    );
  }, [localizedAllExercises, exerciseSearch]);

  const handleAddSet = () => {
    setNewExerciseSets([...newExerciseSets, { reps: '', weight: '' }]);
  };

  const handleRemoveSet = (index) => {
    setNewExerciseSets(newExerciseSets.filter((_, idx) => idx !== index));
  };

  const handleSetChange = (index, field, value) => {
    const newSets = [...newExerciseSets];
    newSets[index] = { ...newSets[index], [field]: value };
    setNewExerciseSets(newSets);
  };

  return (
    <>
      <FullScreenModal open={show} onOpenChange={onHide}>
        <FullScreenModalContent className="flex flex-col">
          <FullScreenModalHeader>
            <FullScreenModalBackButton onClick={onHide} />
            <FullScreenModalTitle>План тренировки</FullScreenModalTitle>
            <Button
              variant="ghost"
              onClick={handleSave}
              className="text-primary font-medium -mr-2 min-w-[60px]"
            >
              Готово
            </Button>
          </FullScreenModalHeader>

          <FullScreenModalBody>
            <div className="space-y-4 pb-6">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={exerciseList.map(ex => ex.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-3">
                    {exerciseList.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <Dumbbell className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>Нет упражнений</p>
                        <p className="text-sm">Нажмите "Добавить упражнение" чтобы начать</p>
                      </div>
                    ) : (
                      exerciseList.map((exercise, index) => (
                        <SortableExerciseItem
                          key={exercise.id}
                          exercise={exercise}
                          index={index}
                          onEdit={() => {}}
                          onDelete={handleDeleteExercise}
                          onUpdate={handleUpdateExercise}
                        />
                      ))
                    )}
                  </div>
                </SortableContext>
              </DndContext>

              <div className="space-y-2">
                {onPlanSelect && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => setShowPlanSelector(true)}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Выбрать план
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => setShowAddExercise(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Добавить упражнение
                </Button>
              </div>
            </div>
          </FullScreenModalBody>
        </FullScreenModalContent>
      </FullScreenModal>

      {/* Панель добавления упражнения */}
      <FullScreenModal open={showAddExercise} onOpenChange={setShowAddExercise}>
        <FullScreenModalContent className="flex flex-col">
          <FullScreenModalHeader>
            <FullScreenModalBackButton
              onClick={() => {
                setShowAddExercise(false);
                setSelectedExercise(null);
                setNewExerciseSets([{ reps: '', weight: '' }]);
                setExerciseSearch('');
              }}
            />
            <FullScreenModalTitle>Добавить упражнение</FullScreenModalTitle>
            <Button
              variant="ghost"
              onClick={handleAddExercise}
              disabled={!selectedExercise}
              className="text-primary font-medium -mr-2 min-w-[60px]"
            >
              Добавить
            </Button>
          </FullScreenModalHeader>

          <FullScreenModalBody>
          <div className="space-y-4 pb-6">
            <div className="text-sm text-muted-foreground">
              Выберите упражнение и укажите подходы
            </div>
            <div className="space-y-2">
              <Label>Упражнение</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between"
                  >
                    {selectedExercise
                      ? (typeof selectedExercise === 'string'
                          ? selectedExercise
                          : selectedExercise.label || selectedExercise.value)
                      : 'Выберите упражнение...'}
                    <Dumbbell className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput
                      placeholder="Поиск упражнений..."
                      value={exerciseSearch}
                      onValueChange={setExerciseSearch}
                    />
                    <CommandList>
                      <CommandEmpty>Упражнения не найдены</CommandEmpty>
                      <CommandGroup>
                        {filteredExercises.map((exercise) => {
                          const exerciseLabel = exercise.label || exercise.value || exercise.name;
                          const isSelected =
                            selectedExercise &&
                            typeof selectedExercise !== 'string' &&
                            (selectedExercise.value || selectedExercise.name) === exercise.value;
                          return (
                            <CommandItem
                              key={exercise.value || exercise.id || exerciseLabel}
                              value={exercise.value || exerciseLabel}
                              onSelect={() => setSelectedExercise(exercise)}
                            >
                              <Dumbbell className="mr-2 h-4 w-4" />
                              {exerciseLabel}
                              {isSelected && <Check className="ml-auto h-4 w-4" />}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Подходы</Label>
              {newExerciseSets.map((set, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <span className="text-sm text-muted-foreground w-16">Подход {index + 1}</span>
                  <Input
                    type="number"
                    placeholder="Повторения"
                    value={set.reps}
                    onChange={(e) => handleSetChange(index, 'reps', e.target.value)}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    placeholder="Вес (кг)"
                    value={set.weight}
                    onChange={(e) => handleSetChange(index, 'weight', e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveSet(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddSet}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Добавить подход
              </Button>
            </div>

          </div>
          </FullScreenModalBody>
        </FullScreenModalContent>
      </FullScreenModal>

        {/* Модал выбора плана */}
        {onPlanSelect && (
          <PlanAssignmentModal
            show={showPlanSelector}
            onHide={() => setShowPlanSelector(false)}
            onPlanSelect={handlePlanSelectFromModal}
            onManualCreate={null}
            trainerId={trainerId}
            currentPlanId={null}
          />
        )}
      </>
    );
  };
  
  export default ExercisePlanModal;

